import React, { useState, useRef, useEffect } from 'react';
import { Box, Divider, Typography, Switch, Tooltip, IconButton, Popover } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  WebOutlined, AddOutlined, DeleteOutlineOutlined,
  DragIndicatorOutlined, CheckOutlined,
  SearchOutlined, LinkOutlined, DescriptionOutlined,
  ArticleOutlined, MeetingRoomOutlined, OpenInNewOutlined,
  CloseOutlined, HowToRegOutlined,
} from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { InspectorSection, StyleRow, StyleInput, ColorInput, PanelHeader } from '../ui/primitives';
import { LayoutModeBanner } from './LayoutModeBanner';
import { MediaField } from '../../lobbyBuilderTab/MediaField';
import { ContentBrowserModal } from '../../lobbyBuilderTab/ContentBrowserModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 9); }

function SectionDivider() {
  return <Divider sx={{ borderColor: '#f0f2f5', my: 0.5 }} />;
}

// Shared native select style
const selectSx = {
  border: '1px solid #e4e9f0',
  borderRadius: '6px',
  px: 1,
  py: '4px',
  fontSize: '0.72rem',
  fontFamily: 'inherit',
  background: '#f8f9fb',
  color: '#1a1a2e',
  outline: 'none',
  cursor: 'pointer',
  width: '100%',
  '&:focus': { borderColor: PRIMARY },
};

// ─── Template picker — full-width rows ────────────────────────────────────────
function TemplatePicker({ value, onChange }) {
  const templates = [
    {
      key: 'classic',
      label: 'Classic',
      desc: '3-column full-width bar',
      preview: (
        <Box sx={{ width: '100%', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, background: '#fff', border: '1px solid #e8edf2' }}>
          <Box sx={{ width: 28, height: 10, borderRadius: 1, background: '#d4dbe6' }} />
          {/* <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[32, 32, 32].map((w, i) => <Box key={i} sx={{ width: w, height: 6, borderRadius: 0.5, background: '#e4e9f0' }} />)}
          </Box> */}
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Box sx={{ width: 26, height: 6, borderRadius: 0.5, background: alpha(PRIMARY, 0.4) }} />
            <Box sx={{ width: 44, height: 16, borderRadius: 1, background: PRIMARY, opacity: 0.85 }} />
          </Box>
        </Box>
      ),
    },
    {
      key: 'pill',
      label: 'Pill',
      desc: 'Floating pill container',
      preview: (
        <Box sx={{ width: '100%', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, background: '#eef3f8'}}>
          <Box sx={{ flex: 1, height: 24, borderRadius: '999px', background: '#fff', border: '1px solid #e0e6ee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, gap: 1, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
            <Box sx={{ width: 22, height: 8, borderRadius: 1, background: '#d4dbe6' }} />
            {/* <Box sx={{ display: 'flex', gap: 0.6 }}>
              {[26, 26, 26].map((w, i) => <Box key={i} sx={{ width: w, height: 5, borderRadius: 0.5, background: '#e4e9f0' }} />)}
            </Box> */}
            <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
              <Box sx={{ width: 20, height: 5, borderRadius: 0.5, background: alpha(PRIMARY, 0.4) }} />
              <Box sx={{ width: 36, height: 16, borderRadius: '999px', background: PRIMARY, opacity: 0.85 }} />
            </Box>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {templates.map(t => {
        const active = value === t.key;
        return (
          <Box
            key={t.key}
            onClick={() => onChange(t.key)}
            sx={{
              width: '100%',
              cursor: 'pointer',
              borderRadius: 1.5,
              p: 1,
              border: active ? `2px solid ${PRIMARY}` : '2px solid #e8edf2',
              background: active ? alpha(PRIMARY, 0.03) : '#fafbfc',
              transition: 'all 0.15s',
              '&:hover': { borderColor: alpha(PRIMARY, 0.4) },
            }}
          >
            {/* Preview thumbnail — full width */}
            {t.preview}

            {/* Label row below the thumbnail */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 600, color: active ? PRIMARY : '#5a6474' }}>
                  {t.label}
                </Typography>
                <Typography sx={{ fontSize: '0.63rem', color: '#9ea8b3', mt: 0.1 }}>{t.desc}</Typography>
              </Box>
              {active && <CheckOutlined sx={{ fontSize: 13, color: PRIMARY }} />}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Link type options ────────────────────────────────────────────────────────
const LINK_TYPE_OPTIONS = [
  { value: 'external', label: 'External URL' },
  { value: 'section',  label: 'Page section'  },
  { value: 'page',     label: 'Page'  },
  { value: 'form',     label: 'Form'  },
  { value: 'lobby',    label: 'Virtual Event' },
];

// ─── LogoPopup — MUI Popover with content browser ────────────────────────────
function LogoPopup({ anchor, config, onUpdate, onClose }) {
  const [contentBrowserOpen, setContentBrowserOpen] = useState(false);
  const upd = (patch) => onUpdate(patch);

  return (
    <>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={contentBrowserOpen ? undefined : onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, width: 240, borderRadius: 2.5, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', border: '1px solid #e4e9f0', p: 1.5 } } }}
      >
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
          Logo Image
        </Typography>

        {/* Current logo preview or choose button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
          {config.logoSrc ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, background: '#f5f7fa', border: '1px solid #e4e9f0', overflow: 'hidden' }}>
              <Box component="img" src={config.logoSrc} sx={{ height: 24, width: 'auto', objectFit: 'contain', maxWidth: 120 }} />
              <Box onClick={() => upd({ logoSrc: '' })} sx={{ ml: 'auto', cursor: 'pointer', color: '#c4cdd8', fontSize: 12, '&:hover': { color: '#dc2626' } }}>✕</Box>
            </Box>
          ) : (
            <Box onClick={() => setContentBrowserOpen(true)}
              sx={{ flex: 1, py: 0.75, borderRadius: 1, border: '1px dashed #d4dbe6', background: '#f8f9fb', cursor: 'pointer', fontSize: '0.72rem', color: '#9ea8b3', textAlign: 'center', '&:hover': { borderColor: PRIMARY, color: PRIMARY } }}>
              Choose image…
            </Box>
          )}
          {config.logoSrc && (
            <Box onClick={() => setContentBrowserOpen(true)} sx={{ px: 1, py: 0.5, borderRadius: 1, border: '1px solid #e4e9f0', cursor: 'pointer', fontSize: '0.65rem', color: '#5a6474', whiteSpace: 'nowrap', '&:hover': { background: '#f0f4f8' } }}>
              Change
            </Box>
          )}
        </Box>

        {/* Alt text + height when logo set */}
        {config.logoSrc && (
          <>
            <Box sx={{ borderTop: '1px solid #f0f2f5', pt: 1, mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Alt text</Typography>
              <Box component="input" value={config.logoAlt || ''} onChange={e => upd({ logoAlt: e.target.value })} placeholder="Company logo"
                sx={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '5px', fontSize: '0.75rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Height</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="input" type="number" value={config.logoHeight || 36} min={16} max={80} onChange={e => upd({ logoHeight: Number(e.target.value) })}
                  sx={{ width: 56, border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '5px', fontSize: '0.75rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>px</Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Logo text when no image */}
        {!config.logoSrc && (
          <Box sx={{ borderTop: '1px solid #f0f2f5', pt: 1 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Text fallback</Typography>
            <Box component="input" value={config.logoText || ''} onChange={e => upd({ logoText: e.target.value })} placeholder="My Event"
              sx={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '5px', fontSize: '0.75rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }} />
          </Box>
        )}
      </Popover>

      {contentBrowserOpen && (
        <ContentBrowserModal
          open
          onClose={() => setContentBrowserOpen(false)}
          mediaType="image"
          onSelect={url => { upd({ logoSrc: url }); setContentBrowserOpen(false); }}
        />
      )}
    </>
  );
}

// ─── Inline-editable label for collapsed link row ─────────────────────────────
function InlineLabel({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <Box
        component="input"
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        onClick={e => e.stopPropagation()}
        sx={{
          flex: 1, border: 'none', borderBottom: `1px solid ${PRIMARY}`, outline: 'none',
          background: 'transparent', fontSize: '0.75rem', fontWeight: 500, color: '#1a1a2e',
          fontFamily: 'inherit', p: 0, minWidth: 0,
        }}
      />
    );
  }

  return (
    <Typography
      onDoubleClick={e => { e.stopPropagation(); setEditing(true); }}
      title="Double-click to rename"
      sx={{
        flex: 1, fontSize: '0.75rem', fontWeight: 500, color: '#1a1a2e',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        cursor: 'text', minWidth: 0,
      }}
    >
      {value || 'Untitled link'}
    </Typography>
  );
}

// ─── Single link row ──────────────────────────────────────────────────────────
function LinkRow({ link, allPages, allSections, allForms = [], allLobbies = [], onChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const update = (patch) => onChange({ ...link, ...patch });

  return (
    <Box
      sx={{
        border: '1px solid #e8edf2', borderRadius: 1.5, overflow: 'hidden',
        background: expanded ? '#fafbfc' : '#fff',
        transition: 'background 0.1s',
      }}
    >
      {/* ── Collapsed row: drag handle | label (editable) | delete ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.7 }}>
        <DragIndicatorOutlined sx={{ fontSize: 13, color: '#d4dbe6', cursor: 'grab', flexShrink: 0 }} />

        {/* Double-click-to-edit label */}
        <InlineLabel value={link.label || ''} onChange={v => update({ label: v })} />

        {/* Expand toggle — small caret on the right */}
        <Box
          onClick={() => setExpanded(v => !v)}
          sx={{
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 20, height: 20, borderRadius: 0.75, cursor: 'pointer', color: '#c0c8d4',
            transition: 'all 0.1s',
            '&:hover': { background: '#f0f4f8', color: '#7a8494' },
          }}
        >
          <Box
            sx={{
              width: 0, height: 0,
              borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
              borderTop: expanded ? 'none' : '5px solid currentColor',
              borderBottom: expanded ? '5px solid currentColor' : 'none',
              mt: expanded ? 0 : '2px', mb: expanded ? '2px' : 0,
            }}
          />
        </Box>

        <Tooltip title="Remove link">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            sx={{ p: 0.3, flexShrink: 0, color: '#c0c8d4', '&:hover': { color: '#dc2626' } }}
          >
            <DeleteOutlineOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Expanded editor ── */}
      {expanded && (
        <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75, borderTop: '1px solid #f0f2f5' }}>

          {/* Type dropdown */}
          <StyleRow label="Type">
            <Box
              component="select"
              value={link.type || 'external'}
              onChange={e => update({ type: e.target.value, href: '' })}
              sx={selectSx}
            >
              {LINK_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Box>
          </StyleRow>

          {/* Destination — varies by type */}
          {(!link.type || link.type === 'external') && (
            <StyleRow label="URL">
              <StyleInput value={link.href || ''} onChange={v => update({ href: v })} placeholder="https://…" />
            </StyleRow>
          )}

          {link.type === 'section' && (
            <StyleRow label="Section">
              <Box component="select" value={link.href || ''} onChange={e => update({ href: e.target.value })} sx={selectSx}>
                <option value="">— pick a section —</option>
                {allSections.map(s => (
                  <option key={s.id} value={`#section-${s.id}`}>{s.label || 'Untitled section'}</option>
                ))}
              </Box>
            </StyleRow>
          )}

          {link.type === 'page' && (
            <StyleRow label="Page">
              <Box component="select" value={link.href || ''} onChange={e => update({ href: e.target.value })} sx={selectSx}>
                <option value="">— pick a page —</option>
                {allPages.map(p => (
                  <option key={p._id} value={`/${p.slug}`}>{p.title}</option>
                ))}
              </Box>
            </StyleRow>
          )}

          {link.type === 'form' && (
            <StyleRow label="Form">
              <Box component="select" value={link.href || ''} onChange={e => update({ href: e.target.value })} sx={selectSx}>
                <option value="">— pick a form —</option>
                {allForms.map(f => (
                  <option key={f._id} value={`/${f.slug}`}>{f.name}</option>
                ))}
              </Box>
            </StyleRow>
          )}

          {link.type === 'lobby' && (
            <StyleRow label="Virtual Event">
              <Box component="select" value={link.href || ''} onChange={e => update({ href: e.target.value })} sx={selectSx}>
                <option value="">— pick a lobby —</option>
                {allLobbies.map(l => (
                  <option key={l._id} value={`/${l.slug}`}>{l.name}</option>
                ))}
              </Box>
            </StyleRow>
          )}

          {/* New tab — only for external */}
          {(!link.type || link.type === 'external') && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#7a8494' }}>Open in new tab</Typography>
              <Switch
                checked={!!link.newTab}
                onChange={e => update({ newTab: e.target.checked })}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Links list ───────────────────────────────────────────────────────────────
function LinksEditor({ links, allPages, allSections, allForms = [], allLobbies = [], onChange }) {
  const addLink = () => onChange([...links, { id: uid(), label: 'New Link', type: 'external', href: '', newTab: false }]);
  const updateLink = (i, patch) => onChange(links.map((l, idx) => idx === i ? patch : l));
  const deleteLink = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <InspectorSection label="Navigation Links">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {links.length === 0 && (
          <Typography sx={{ fontSize: '0.72rem', color: '#c0c8d4', textAlign: 'center', py: 1, fontStyle: 'italic' }}>
            No links yet
          </Typography>
        )}
        {links.map((link, i) => (
          <LinkRow
            key={link.id || i}
            link={link}
            allPages={allPages}
            allSections={allSections}
            allForms={allForms}
            allLobbies={allLobbies}
            onChange={patch => updateLink(i, patch)}
            onDelete={() => deleteLink(i)}
          />
        ))}
        <Box
          onClick={addLink}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            py: 0.7, borderRadius: 1.5, border: '1px dashed #c4cdd8', cursor: 'pointer',
            fontSize: '0.72rem', fontWeight: 600, color: '#9ea8b3', mt: 0.25,
            transition: 'all 0.15s',
            '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.04) },
          }}
        >
          <AddOutlined sx={{ fontSize: 14 }} />
          Add link
        </Box>
      </Box>
    </InspectorSection>
  );
}

// ─── Color row: label above, swatch + input below ────────────────────────────
function ColorRow({ label, value, onChange }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#7a8494', mb: 0.4, fontWeight: 500 }}>{label}</Typography>
      <ColorInput value={value} onChange={onChange} />
    </Box>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// RegisterLinkPicker — compact popover below the trigger button
// ─────────────────────────────────────────────────────────────────────────────
function RegisterLinkPicker({ anchor, onClose, onSelect, allPages = [], allForms = [], allLobbies = [], currentHref }) {
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (anchor) { setQuery(''); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [anchor]);

  const q = query.toLowerCase().trim();
  const isExternal = (h) => h && (h.startsWith('http://') || h.startsWith('https://'));

  const SECTIONS = [
    {
      key:   'forms',
      label: 'Forms',
      items: allForms.filter(f => !q || f.name?.toLowerCase().includes(q)).map(f => ({
        id: f._id, label: f.name, href: `/${f.slug}`, type: 'form',
        isRegistration: f.isRegistration,
      })),
    },
    {
      key:   'pages',
      label: 'Pages',
      items: allPages.filter(p => !q || p.title?.toLowerCase().includes(q)).map(p => ({
        id: p._id, label: p.title, href: `/${p.slug}`, type: 'page',
      })),
    },
    {
      key:   'lobbies',
      label: 'Virtual Events',
      items: allLobbies.filter(l => !q || l.name?.toLowerCase().includes(q)).map(l => ({
        id: l._id, label: l.name, href: `/${l.slug}`, type: 'lobby',
        bgUrl: l.bgUrl || null,
      })),
    },
  ];

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { mt: 0.75, width: 280, maxHeight: 420, borderRadius: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e8edf2', display: 'flex', flexDirection: 'column', overflow: 'hidden' } } }}
    >
      {/* Search */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, border: '1.5px solid #e4e9f0', borderRadius: 1.5, px: 1, py: 0.65, background: '#f8f9fb', '&:focus-within': { borderColor: PRIMARY, background: '#fff' } }}>
          <SearchOutlined sx={{ fontSize: 14, color: '#9ea8b3', flexShrink: 0 }} />
          <Box ref={inputRef} component="input" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search or paste URL…"
            sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.78rem', color: '#1a1a2e', '&::placeholder': { color: '#c0c8d4' } }} />
        </Box>
        {/* External URL */}
        {query && isExternal(query) && (
          <Box onClick={() => { onSelect({ href: query, label: null, type: 'external', isRegistration: false }); onClose(); }}
            sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.65, borderRadius: 1.25, background: alpha(PRIMARY, 0.06), border: `1px solid ${alpha(PRIMARY, 0.18)}`, cursor: 'pointer', '&:hover': { background: alpha(PRIMARY, 0.1) } }}>
            <OpenInNewOutlined sx={{ fontSize: 13, color: PRIMARY, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Link to: {query}</Typography>
          </Box>
        )}
      </Box>

      {/* Sections */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5 }}>
        {SECTIONS.map((sec, si) => (
          <Box key={sec.key} sx={{ mb: si < SECTIONS.length - 1 ? 2 : 0 }}>
            {/* Section title — no icon */}
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
              {sec.label}
            </Typography>
            {sec.items.length === 0 ? (
              <Typography sx={{ fontSize: '0.7rem', color: '#c0c8d4', fontStyle: 'italic', pl: 0.25 }}>No {sec.label.toLowerCase()} found</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {sec.items.map(item => {
                  const isCurrent = currentHref === item.href;
                  return (
                    <Box key={item.id} onClick={() => { onSelect({ ...item }); onClose(); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 1.5, border: `1.5px solid ${isCurrent ? PRIMARY : '#e4e9f0'}`, background: isCurrent ? alpha(PRIMARY, 0.04) : '#fff', cursor: 'pointer', overflow: 'hidden', transition: 'all 0.12s', '&:hover': { borderColor: PRIMARY, background: alpha(PRIMARY, 0.04) } }}>

                      {/* Lobby thumbnail */}
                      {item.type === 'lobby' && (
                        <Box sx={{ width: 44, height: 36, flexShrink: 0, background: item.bgUrl ? `url('${item.bgUrl}') center/cover` : 'linear-gradient(135deg,#1a2540,#2d4a6b)', borderRadius: '6px 0 0 6px' }} />
                      )}

                      {/* Label + badges */}
                      <Box sx={{ flex: 1, px: item.type === 'lobby' ? 0.75 : 1, py: 0.875, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isCurrent ? PRIMARY : '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                          {item.label}
                        </Typography>
                        {item.isRegistration && (
                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.25 }}>Registration</Typography>
                        )}
                      </Box>

                      {isCurrent && <CheckOutlined sx={{ fontSize: 13, color: PRIMARY, flexShrink: 0, mr: 0.75 }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Popover>
  );
}

export function HeaderInspector({ section, onUpdate, onClose, allPages, allSections, isOverride, onEnableOverride, onDisableOverride, allForms = [], allLobbies = [], event }) {
  const config = section.headerConfig || {};
  const [registerPickerAnchor, setRegisterPickerAnchor] = useState(null);
  const [logoAnchor, setLogoAnchor] = useState(null);

  const updateConfig = (patch) => onUpdate({ ...section, headerConfig: { ...config, ...patch } });
  const updateStyle  = (patch) => updateConfig({ style: { ...(config.style || {}), ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<WebOutlined sx={{ fontSize: 14 }} />} label="Header" color={PRIMARY} onClose={onClose} />

      {/* ── Global / Custom mode banner ── */}
      {/* <LayoutModeBanner
        isOverride={isOverride}
        label="header"
        onEnableOverride={onEnableOverride}
        onDisableOverride={onDisableOverride}
      /> */}

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Template ── */}
        <InspectorSection label="Template">
          <TemplatePicker
            value={config.template || 'classic'}
            onChange={v => updateConfig({ template: v })}
          />

          {/* Sticky toggle — shown for both templates */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.25, pt: 1, borderTop: '1px solid #f0f2f5' }}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e' }}>Sticky header</Typography>
              <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3', mt: 0.15 }}>Stays fixed when scrolling</Typography>
            </Box>
            <Switch
              checked={!!config.sticky}
              onChange={e => updateConfig({ sticky: e.target.checked })}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
              }}
            />
          </Box>

          {/* Pill-only: top offset */}
          {(config.template === 'pill') && (
            <Box sx={{ mt: 1 }}>
              <StyleRow label="Top offset">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    component="input"
                    type="number"
                    value={config.style?.pillTop ?? 16}
                    min={0} max={80}
                    onChange={e => updateStyle({ pillTop: Number(e.target.value) })}
                    sx={{ width: 56, border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '4px', fontSize: '0.72rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }}
                  />
                  <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>px from top</Typography>
                </Box>
              </StyleRow>
            </Box>
          )}
        </InspectorSection>

        <SectionDivider />

        {/* ── Logo ── */}
        <InspectorSection label="Logo">
          <Box
            onClick={e => setLogoAnchor(e.currentTarget)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.875, border: '1.5px solid #e4e9f0', borderRadius: 1.5, cursor: 'pointer', background: '#f8f9fb', transition: 'all 0.15s', '&:hover': { borderColor: PRIMARY, background: '#fff' } }}
          >
            {config.logoSrc ? (
              <Box component="img" src={config.logoSrc} sx={{ height: 24, width: 'auto', maxWidth: 100, objectFit: 'contain' }} />
            ) : (
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: alpha(PRIMARY, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: PRIMARY }} />
              </Box>
            )}
            <Typography sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 500, color: config.logoSrc ? '#1a1a2e' : '#9ea8b3' }}>
              {config.logoSrc ? 'Logo set — click to change' : 'No logo — click to add'}
            </Typography>
          </Box>
          <LogoPopup anchor={logoAnchor} config={config} onUpdate={updateConfig} onClose={() => setLogoAnchor(null)} />
        </InspectorSection>

        <SectionDivider />

        {/* ── Navigation links ── */}
        <LinksEditor
          links={config.links || []}
          allPages={allPages || []}
          allSections={allSections || []}
          allForms={allForms}
          allLobbies={allLobbies}
          onChange={links => updateConfig({ links })}
        />

        <SectionDivider />

        {/* ── Register Button ── */}
        <InspectorSection label="Register Button">
          {/* Inline editable label */}
          <StyleRow label="Label">
            <StyleInput
              value={config.registerLabel || ''}
              onChange={v => updateConfig({ registerLabel: v })}
              placeholder="Register Now"
            />
          </StyleRow>

          {/* Link picker */}
          <StyleRow label="Links to">
            <Box
              onClick={e => setRegisterPickerAnchor(e.currentTarget)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, border: '1.5px solid #e4e9f0', borderRadius: 1.5, cursor: 'pointer', background: '#f8f9fb', transition: 'all 0.15s', '&:hover': { borderColor: PRIMARY, background: '#fff' } }}
            >
              <LinkOutlined sx={{ fontSize: 14, color: '#9ea8b3', flexShrink: 0 }} />
              <Typography sx={{ flex: 1, fontSize: '0.78rem', color: config.registerHref ? '#1a1a2e' : '#c0c8d4', fontFamily: config.registerHref ? 'monospace' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {config.registerHref || 'Set destination…'}
              </Typography>
              {config.registerHref && (
                <Box onClick={e => { e.stopPropagation(); updateConfig({ registerHref: '', registerIsRegistration: false }); }}
                  sx={{ color: '#c0c8d4', '&:hover': { color: '#ef4444' }, display: 'flex', flexShrink: 0 }}>
                  <CloseOutlined sx={{ fontSize: 13 }} />
                </Box>
              )}
            </Box>
          </StyleRow>

          {/* Login button visibility — only shown when NOT a registration form */}
          <StyleRow label="Show login">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box onClick={() => updateConfig({ showLogin: !config.showLogin })}
                sx={{ width: 32, height: 18, borderRadius: 9, background: config.showLogin !== false ? PRIMARY : '#d1d9e0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <Box sx={{ position: 'absolute', top: 1, left: config.showLogin !== false ? 15 : 1, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
              </Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3' }}>
                {config.registerIsRegistration ? 'Always shown for registration forms' : config.showLogin !== false ? 'Shown' : 'Hidden'}
              </Typography>
            </Box>
          </StyleRow>
        </InspectorSection>

        <RegisterLinkPicker
          anchor={registerPickerAnchor}
          onClose={() => setRegisterPickerAnchor(null)}
          allPages={allPages}
          allForms={allForms}
          allLobbies={allLobbies}
          currentHref={config.registerHref}
          onSelect={({ href, label, isRegistration }) => {
            const patch = { registerHref: href };
            if (label && !config.registerLabel) patch.registerLabel = label;
            patch.registerIsRegistration = !!isRegistration;
            // If registration form → always show login; otherwise default true
            if (isRegistration) patch.showLogin = true;
            updateConfig(patch);
          }}
        />

        <SectionDivider />

        {/* ── Colors — label above, swatch + input below ── */}
        <InspectorSection label="Colors">
          <ColorRow
            label="Background"
            value={config.style?.background || '#ffffff'}
            onChange={v => updateStyle({ background: v })}
          />
          <ColorRow
            label="Nav link color"
            value={config.style?.linkColor || '#1a1a2e'}
            onChange={v => updateStyle({ linkColor: v })}
          />
          <ColorRow
            label="Login text color"
            value={config.style?.loginColor || '#1075BA'}
            onChange={v => updateStyle({ loginColor: v })}
          />
          <ColorRow
            label="Register button background"
            value={config.style?.registerBg || '#1075BA'}
            onChange={v => updateStyle({ registerBg: v })}
          />
          <ColorRow
            label="Register button text"
            value={config.style?.registerColor || '#ffffff'}
            onChange={v => updateStyle({ registerColor: v })}
          />
          {config.template === 'pill' && (
            <ColorRow
              label="Pill background"
              value={config.style?.pillBg || '#ffffff'}
              onChange={v => updateStyle({ pillBg: v })}
            />
          )}
        </InspectorSection>

      </Box>
    </Box>
  );
}